import { createSlice } from '@reduxjs/toolkit';
import { AccountStatus } from '../../constants/userStatus';
import { getAllApps, getCodeSearchListData, getCompanyDetails, getCustomerCodeDataList, getGroupTypes, getGroupbyTypes, getMatchCodeListData, getUserDataList } from './MappingActions';

interface ICompanyDetails{
  companyName: string;
  phoneNo:string;
  taxId:string;
  address:string;
  country:string;
  partnerType:string;
}

interface IEditUser{
  email: string;
  name:string;
  group:Array<string>;
  password:string;
  pwdExpired: '' | 'Y' | 'N';
  // status represents AccountStatus id from backend (0=Inactive, 1=Active, 2=Submitted, 3=Pending Verification, 4=Locked, 5=Password Expired, 6=Rejected)
  status: number;
  country:string;
  id: string|null,
  grpType: Array<string>,
  isRootUser: string,
  title: string,
  userGrpName: Array<string>,
  phnNo: string|null,
  loginAttempt: number,
  countryAccess: string,
  locationAccess: string,
  appName: Array<string>,
}

interface IMappingUserList{
  id: string;
  email:string;
  name:string;
  group: Array<string>;
  status: number;
  // pwdExpired flag comes from backend ('Y' | 'N')
  pwdExpired: string;
  country: string;
  grpType: Array<string>,
  isRootUser: string,
  title: string,
  userGrpName: Array<string>,
  mstPartnerId: string,
  phnNo: string,
  loginAttempt: number,
  countryAccess: string,
  locationAccess: string,
  appName: Array<string>,
}

interface ICustomerCodeList{
  liner: string;
  customerCode: string;
  matchCode: string;
  id:string;
}

interface IAddCustomerCodeList{
  id: string,
  liner:string;
  customerCode: string;
  matchCode: string;
  name: string;
  address: string;
  taxId: string;
  country: string;
  mstPartnerId: string;
  selected: boolean;
}

interface IMatchCodeList{
   mstUsrId:string;
  matchCode: string;
  customerCode: string;
  email: string;
  name: string;
  mstPartnerId: string;
  selected: boolean;
}

interface IMappingDashSlice {
  showColumnSettings:boolean;
  mappingTopSearch: string;
  userListData: Array<IMappingUserList>,
  groupList: Array<any>,
  appList: Array<{ value: string; label: string }>,
  companyDetails: ICompanyDetails;
  userEditingState: IEditUser;
  customerCodeDataList: Array<ICustomerCodeList>;
  codeSearchListData: Array<IAddCustomerCodeList>|null;
  addCustomerCodeFilter:{
    liner:string;
    code:string;
  },
  matchCodeList: Array<IMatchCodeList>|null
}

const initialState: IMappingDashSlice = {
  showColumnSettings:false,
  mappingTopSearch: '',
  userListData:[],
  groupList:[],
  appList: [],
  companyDetails:{
    companyName:'',
    phoneNo:'',
    address:'',
    taxId:'',
    country:'',
    partnerType:''
  },
    userEditingState:{
    name: '',
    email: '',
    password: '',
    pwdExpired: 'N',
    group: [],
    country: '',
      status: AccountStatus.PENDING_VERIFICATION,
    id: '',
    grpType: [],
    isRootUser: '',
    title: '',
    userGrpName: [],
    phnNo: '',
    loginAttempt: 0,
    countryAccess: '',
    locationAccess: '',
    appName: []
  },
  customerCodeDataList:[],
  codeSearchListData:null,
  addCustomerCodeFilter:{
    liner:'',
    code:''
  },
  matchCodeList: []
};

export const mappingDashboardSlice = createSlice({
  name: 'mappingDashboard',
  initialState,
  reducers: {
    toggleColumnSettingsPayDash:(state,action)=>{
        state.showColumnSettings = action.payload
    },
    setMappingTopSearchFilter:(state, action)=>{
      state.mappingTopSearch = action.payload
    },
    setCompanyDetails:(state:any,action)=>{
      Object.entries(action.payload).forEach(([key,val])=>{
        if(Object.hasOwn(state.companyDetails,key))state.companyDetails[key]=val
      })
    },
    setUserEditDetails:(state:any,action)=>{
      Object.entries(action.payload).forEach(([key,val])=>{
        if(Object.hasOwn(state.userEditingState,key))state.userEditingState[key]=val
      })
    },
     populateEditUserData:(state, action)=>{
       const row = state.userListData.find((i)=>action.payload == i.id)
       if(!row){
         state.userEditingState = {
           name: '',
           email: '',
           password: '',
           pwdExpired: 'N',
           group: [],
           country: state.companyDetails.country,
           status: AccountStatus.PENDING_VERIFICATION,
           id: null,
           grpType: [],
           isRootUser: '',
           title: '',
           userGrpName: [],
           phnNo: null,
           loginAttempt: 0,
           countryAccess: '',
           locationAccess: '',
           appName: []
      }
       return
     }
       state.userEditingState = {
         ...row,
         password: '',
         // Explicit check — only 'Y' sets expired, everything else is 'N'
         pwdExpired: row.pwdExpired === 'Y' ? 'Y' : 'N',
         // preserve numeric status from backend (AccountStatus id), fallback to 1 (Active) if not defined
         status: (typeof (row as any).status !== 'undefined') ? Number((row as any).status) : (state.userEditingState.status ?? AccountStatus.ACTIVE),
         appName: row.appName ?? []
       }
     },

    selectCustomerCode: (state, action)=>{
      const row = state.codeSearchListData?.find((i)=>i.id == action.payload.id)
      if(!row) return
      row.selected = action.payload.val
    },

    selectMappingUsers: (state, action)=>{
      const row = state.matchCodeList?.find((i)=>i.mstUsrId == action.payload.mstUsrId)
      if(!row) return
      row.selected = action.payload.val
    },

    setAddCodeFilters: (state:any, action)=>{
      Object.entries(action.payload).forEach(([key,val])=>{
        if(Object.hasOwn(state.addCustomerCodeFilter, key)) state.addCustomerCodeFilter[key] = val
      })
    },
    
    clearMatchCodeList: (state)=>{
      state.matchCodeList = null
    },

    clearCodeSearchList: (state)=>{
      state.codeSearchListData = null
    },
  },
  extraReducers:(builder)=>{
    builder.addCase(getCompanyDetails.pending, (state, action)=>{
      state.companyDetails = {
        companyName:'',
        phoneNo:'',
        address:'',
        taxId:'',
        country:'',
        partnerType:''
      }
    })
    builder.addCase(getCompanyDetails.fulfilled,(state,action:any)=>{
      state.companyDetails = action.payload
    })

    builder.addCase(getUserDataList.pending,(state)=>{
      state.userListData = []
    })
    builder.addCase(getUserDataList.fulfilled,(state, action)=>{
      state.userListData = action.payload
    })

    builder.addCase(getCustomerCodeDataList.pending,(state)=>{
      state.customerCodeDataList = []
    })
    builder.addCase(getCustomerCodeDataList.fulfilled,(state,action)=>{
      state.customerCodeDataList = action.payload
    })

    builder.addCase(getCodeSearchListData.pending, (state,action)=>{
      state.codeSearchListData = []
    })
    builder.addCase(getCodeSearchListData.fulfilled, (state,action)=>{
      state.codeSearchListData = action.payload
    })

    builder.addCase(getMatchCodeListData.pending, (state)=>{
      state.matchCodeList = []
    })
    builder.addCase(getMatchCodeListData.fulfilled, (state, action)=>{
      state.matchCodeList = action.payload
    })

    builder.addCase(getGroupTypes.pending, (state)=>{
      state.groupList = []
    })
    builder.addCase(getGroupTypes.fulfilled, (state, action)=>{
      state.groupList = action.payload
    })
    builder.addCase(getGroupbyTypes.pending, (state)=>{
      state.groupList = []
    })
    builder.addCase(getGroupbyTypes.fulfilled, (state, action)=>{
      state.groupList = action.payload
    })
    builder.addCase(getAllApps.pending, (state)=>{
      state.appList = []
    })
    builder.addCase(getAllApps.fulfilled, (state, action)=>{
      state.appList = action.payload
    })
  }
});

export const { 
  toggleColumnSettingsPayDash,
  setMappingTopSearchFilter,
  setCompanyDetails,
  setUserEditDetails,
  populateEditUserData,
  selectCustomerCode,
  setAddCodeFilters,
  selectMappingUsers,
  clearMatchCodeList,
   clearCodeSearchList
} = mappingDashboardSlice.actions;

export default mappingDashboardSlice.reducer;
